/**
 * Typed client for `/api/fleet/` MVP rally-event surface.
 *
 * Lives separately from `services/api.ts` (the legacy axios client) so
 * these endpoints can be consumed without re-flowing the big shared
 * ApiClient class. Uses native fetch + the same `/api` baseURL
 * convention that App.tsx assumes for dev-proxy + prod-nginx.
 *
 * The backend Event row is intentionally named `Event` here even
 * though the DOM has a built-in `Event` type — local imports either
 * reference it via `EventRow` alias or restrict the import to its
 * usage site. Frontend types/index.ts already uses `FleetEvent` for
 * the WS/SSE event-union; don't conflate.
 */

import { CSRF_HEADER_NAME, UNSAFE_METHODS, readCsrfCookie, redirectToLogin } from './csrf';

const BASE = '/api/fleet';

export interface EventGate {
  id: number;
  stage_index: number;
  kind: 'start' | 'finish' | 'lap_line';
  lat: number;
  lon: number;
  radius_m: number;
  /** lap_line endpoint B — null on start/finish circles. */
  lat2?: number | null;
  lon2?: number | null;
}

export interface EnrollmentCounts {
  pending: number;
  armed: number;
  rejected: number;
  disarmed: number;
}

/** One stage's display/schedule metadata (multi-stage MVP). Decoration
 *  only — gates keyed by stage_index remain the behavioral truth, and
 *  phones never receive this. */
export interface StageMeta {
  index: number; // 0–63
  name: string;
  type?: 'Stage' | 'Circuit' | null;
  start_time?: string | null; // 'HH:MM' event-local
  /** 'YYYY-MM-DD' event-local. null/absent = use the event's first active day. */
  date?: string | null;
  car_interval_seconds?: number | null;
  timeout_seconds?: number | null;
  laps?: number | null;
  reverse_of?: number | null;
}

export interface StagesJson {
  version: number;
  stages: StageMeta[];
}

/** Manual start list: one car's start time for one stage. Keyed by
 *  enrollment_id (the enrollment is today's device↔car pairing). Every
 *  field is behavioral — saving a changed list bumps geofence_revision
 *  and re-emits per-device payloads. */
export interface StartListEntry {
  enrollment_id: number;
  stage_index: number; // 0–63
  start_time: string; // 'HH:MM' event-local
  /** 'YYYY-MM-DD'; null/absent = the stage's date, else the event's opening day. */
  date?: string | null;
}

export interface StartListJson {
  version: number;
  entries: StartListEntry[];
}

export interface EventRow {
  id: number;
  name: string;
  type: string | null;
  location: string | null;
  description: string | null;
  timezone: string | null;
  active_from: string;
  /** Null = open-ended event (no scheduled end). */
  active_until: string | null;
  max_stage_seconds: number;
  /** Multi-stage MVP metadata; null on events that predate it. */
  stages_json?: StagesJson | null;
  /** Manual per-car start list; null when never saved. */
  start_list_json?: StartListJson | null;
  public: boolean;
  /** Public-directory opt-in (exposes the join code to the phone
   *  app's Browse-public list; separate from the viewer gate). */
  listed?: boolean;
  /** Self-service flags — what an individual driver may control
   *  from the phone app (defaults ON). */
  allow_individual_schedule?: boolean;
  allow_individual_quality?: boolean;
  join_code: string | null;
  geofence_revision: number;
  show_raceline: boolean;
  raceline_uploaded_at: string | null;
  raceline_vertex_count: number | null;
  raceline_length_m: number | null;
  created_at: string;
  updated_at: string;
  gates: EventGate[];
  /** Stage indices that carry a corridor route (see StageRouteInfo). */
  stage_route_indices?: number[];
  enrollment_counts: EnrollmentCounts;
}

export interface EventCreate {
  name: string;
  type?: string | null;
  location?: string | null;
  description?: string | null;
  timezone?: string | null;
  active_from: string;
  /** Omit or null = open-ended event. */
  active_until?: string | null;
  max_stage_seconds?: number;
  public?: boolean;
  /** Public-directory opt-in (default OFF server-side). */
  listed?: boolean;
  /** Self-service flags (default ON server-side). */
  allow_individual_schedule?: boolean;
  allow_individual_quality?: boolean;
}

export type EventPatch = Partial<EventCreate> & {
  show_raceline?: boolean;
  /** Multi-stage MVP metadata (validated server-side; cosmetic — no
   *  geofence bump, never reaches phones). */
  stages_json?: StagesJson;
  /** Manual start list (validated server-side; BEHAVIORAL — any
   *  entry change bumps the revision and re-emits per-device). */
  start_list_json?: StartListJson;
  /** Lost-update guard for start-list saves: the geofence_revision the
   *  client's edit was based on. When sent alongside start_list_json
   *  and it no longer matches, the backend rejects the PUT with 409
   *  instead of silently overwriting another operator's save. Pure
   *  precondition — never persisted. */
  start_list_expected_revision?: number;
};

export interface RacelineUploadResult {
  event_id: number;
  vertex_count: number;
  length_m: number;
  uploaded_at: string;
  simplified_from: number;
}

export interface RunSession {
  session_id: string;
  state: string;
  start_utc_ms: number | null;
  end_utc_ms: number | null;
  segment_count: number | null;
  /** Whether this session has at least one playable rendition — the
   *  signal to gate availability on. `segment_count` is a compatibility
   *  high-water mark, not a readiness signal, and can under-report a
   *  playable session, so it must never be used to decide what's shown. */
  has_media: boolean;
  /** The stage this session belongs to; null = unattributed. Tags the
   *  viewer's session list. */
  stage_index?: number | null;
  /** Measured server-received upload throughput for this session. The
   *  backend DTO (`app/api/fleet/runs.py::RunSessionOut`) does not
   *  populate this field yet. Declared here, additively, so callers
   *  that already read it (e.g. `Fleet.tsx`) type-check; it resolves to
   *  `undefined` (→ null) until the backend populates it, matching
   *  current behavior. */
  upload_avg_mbps?: number | null;
}

export interface Run {
  run_id: string;
  device_id: string;
  sessions: RunSession[];
  start_utc_ms: number | null;
  end_utc_ms: number | null;
  manually_triggered: boolean;
  /** Enrollment display labels — null when the enrollment row was
   *  deleted after the sessions landed. */
  driver_name: string | null;
  vehicle: string | null;
  enrollment_id: number | null;
  /** Organizer-assigned race number. Optional: older backends omit it. */
  car_number?: string | null;
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers: Record<string, string> = { ...extraHeaders };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  // Cookie session auth: `credentials: 'same-origin'` makes
  // fetch send the (HttpOnly) session cookie on every call below (the
  // fetch spec's own default for same-origin requests already does
  // this, but stating it explicitly documents the intent rather than
  // relying on an implicit default). The double-submit CSRF header is
  // still required by hand — a cookie being SENT automatically is
  // exactly the threat CSRF defends against, so this class of request
  // must always prove it via the header too. See services/csrf.ts +
  // backend services/auth/csrf.py.
  if (UNSAFE_METHODS.has(method.toLowerCase())) {
    const token = readCsrfCookie();
    if (token) headers[CSRF_HEADER_NAME] = token;
  }
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: Object.keys(headers).length ? headers : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };
  const res = await fetch(`${BASE}${path}`, init);
  if (res.status === 401) redirectToLogin();
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = (await res.json()).detail;
    } catch {
      // ignore parse error; fall through to status-text
    }
    throw new FleetApiError(res.status, typeof detail === 'string' ? detail : res.statusText, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class FleetApiError extends Error {
  readonly status: number;
  readonly detail: unknown;
  constructor(status: number, message: string, detail: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

// ── Events ────────────────────────────────────────────────────────────

export const listEvents = (): Promise<EventRow[]> => call('GET', '/events');
export const getEvent = (id: number): Promise<EventRow> => call('GET', `/events/${id}`);
export const createEvent = (body: EventCreate): Promise<EventRow> => call('POST', '/events', body);
const WINDOW_PATCH_KEYS = ['active_from', 'active_until', 'timezone'] as const;

export const updateEvent = (id: number, patch: EventPatch): Promise<EventRow> => {
  const touchesWindow = WINDOW_PATCH_KEYS.some((k) => k in patch);
  const extraHeaders = touchesWindow ? { 'X-Event-Time-Contract': '2' } : undefined;
  return call('PUT', `/events/${id}`, patch, extraHeaders);
};
// ── Raceline ──────────────────────────────────────────────────────────

export async function uploadRaceline(eventId: number, file: File): Promise<RacelineUploadResult> {
  const body = new FormData();
  body.append('file', file);
  const token = readCsrfCookie();
  const res = await fetch(`${BASE}/events/${eventId}/raceline`, {
    method: 'POST',
    credentials: 'same-origin',
    body,
    headers: token ? { [CSRF_HEADER_NAME]: token } : undefined,
  });
  if (res.status === 401) redirectToLogin();
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = (await res.json()).detail;
    } catch {
      /* fall through */
    }
    throw new FleetApiError(res.status, typeof detail === 'string' ? detail : res.statusText, detail);
  }
  return (await res.json()) as RacelineUploadResult;
}

export const deleteRaceline = (eventId: number): Promise<{ event_id: number; deleted: boolean }> =>
  call('DELETE', `/events/${eventId}/raceline`);

/** Public spectator URL for the raceline GeoJSON. Returns 404 when
 * show_raceline is off — callers must check the EventRow flag before
 * pre-fetching. Admin tooling passes `?include_hidden=1`. */
export const racelineUrl = (eventId: number, includeHidden = false): string =>
  `${BASE}/events/${eventId}/raceline${includeHidden ? '?include_hidden=1' : ''}`;

// ── Sessions and runs ─────────────────────────────────────────────────

export interface UnendedSession {
  session_id: string;
  device_id: string;
  stale_minutes: number;
  artifact_count: number;
}

export const listUnendedSessions = (eventId: number): Promise<UnendedSession[]> =>
  call('GET', `/events/${eventId}/sessions/unended`);

export const forceEndSession = (
  sessionId: string,
): Promise<{ session_id: string; state: string }> =>
  call('POST', `/sessions/${encodeURIComponent(sessionId)}/force-end`);

export const listEventRuns = (eventId: number): Promise<Run[]> =>
  call('GET', `/events/${eventId}/runs`);

// ── GPS: event-window path for one enrolled competitor ────────────────
//
// The legacy (no `v=2`) shape has no client left — `useEventDeviceGps.ts`
// is the only caller and it's on the cursor contract below — but the
// backend route keeps serving it unversioned for any caller outside this
// repo, so it stays documented there even with no `fetch` wrapper here.

export interface EventDeviceGpsPage {
  /** [lat, lon, utc_ms, speed_mps, id], ordered by server id ascending. */
  track: Array<[number, number, number, number, number]>;
  /** First call only: the event window held more rows than the safety cap
   *  (`EVENT_GPS_MAX_POINTS`) and the oldest were dropped — real data loss. */
  truncated: boolean;
  /** First call only: the kept history was thinned by even stride to fit
   *  the map budget. No rows lost — `next_after_id` still covers them. */
  downsampled: boolean;
  /** Incremental calls only: another page is already waiting past
   *  `next_after_id` — poll again now instead of on the next tick. */
  more: boolean;
  next_after_id: number | null;
  reload: boolean;
}

/**
 * v2 (`?v=2`) id-cursor contract for the same GPS history: omit `afterId`
 * for bounded initial history, or pass a prior response's `next_after_id`
 * for only the rows since then. See
 * backend/app/api/fleet/route_attempts.py's `get_event_device_gps`.
 */
export const getEventDeviceGpsPage = (
  eventId: number,
  deviceId: string,
  afterId?: number,
): Promise<EventDeviceGpsPage> => {
  const qs = afterId === undefined ? 'v=2' : `v=2&after_id=${afterId}`;
  return call('GET', `/events/${eventId}/devices/${encodeURIComponent(deviceId)}/gps?${qs}`);
};

// ── Stage sessions (scoped by stage date) ─────────────────────────────

export interface StageSession {
  session_id: string;
  device_id: string;
  state: string;
  started_at: string | null;
  ended_at: string | null;
  start_utc_ms: number | null;
  end_utc_ms: number | null;
  segment_count: number | null;
  manually_triggered: boolean;
  unclean: boolean;
  /** Set = ATTRIBUTED to this stage (device-reported or fallback);
   *  null = same-date unattributed session (legacy continuity). */
  stage_index?: number | null;
  /** Race number inherited via the device's enrollment. */
  car_number?: string | null;
}

/** Sessions recorded on a stage's calendar date (event-local). The stage
 *  date is the stage's stages_json `date`, else the event's first active
 *  day; date math is done server-side in the event timezone. */
export const listStageSessions = (
  eventId: number,
  stageIndex: number,
): Promise<StageSession[]> =>
  call('GET', `/events/${eventId}/stages/${stageIndex}/sessions`);

/**
 * Prepare the stage's footage (optionally one device's), then hand the
 * zip URL to the browser as a plain native download. The prepare call
 * carries the slow first-time assembly (and the auth/401 handling);
 * once it returns, the zip streams from cached files so the browser's
 * download bar appears within seconds.
 */
export async function downloadStageFootage(
  eventId: number,
  stageIndex: number,
  deviceId?: string,
): Promise<void> {
  const qs = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : '';
  await call('POST', `/events/${eventId}/stages/${stageIndex}/footage/prepare${qs}`);
  window.location.assign(
    `${BASE}/events/${eventId}/stages/${stageIndex}/footage.zip${qs}`,
  );
}

// ── Full-length stage video ────────────────────────────────────────────

export type FullVideoState =
  | 'empty'
  | 'waiting'
  | 'idle'
  | 'queued'
  | 'running'
  | 'ready'
  | 'outdated'
  | 'failed';

export interface FullVideoJob {
  requested_by?: string | null;
  requested_at?: string | null;
  phase?: string | null;
  parts_done?: number;
  parts_total?: number;
  cancel_requested?: boolean;
  error?: string | null;
}

export interface FullVideoDownload {
  session_count: number;
  /** False once the car's sessions changed after this file was built. */
  current: boolean;
  cameras: { camera_id: string; size_bytes: number }[];
  notes: string[];
  ready_at: string | null;
}

export interface FullVideoView {
  state: FullVideoState;
  can_prepare: boolean;
  blocked_reasons: string[];
  session_count: number;
  job: FullVideoJob | null;
  download: FullVideoDownload | null;
}

export const listFullVideos = (
  eventId: number,
  stageIndex: number,
): Promise<Record<string, FullVideoView>> =>
  call('GET', `/events/${eventId}/stages/${stageIndex}/full-videos`);

export const getFullVideo = (
  eventId: number,
  stageIndex: number,
  deviceId: string,
): Promise<FullVideoView> =>
  call('GET', `/events/${eventId}/stages/${stageIndex}/devices/${encodeURIComponent(deviceId)}/full-video`);

export const prepareFullVideo = (
  eventId: number,
  stageIndex: number,
  deviceId: string,
): Promise<FullVideoView> =>
  call(
    'POST',
    `/events/${eventId}/stages/${stageIndex}/devices/${encodeURIComponent(deviceId)}/full-video`,
  );

export const cancelFullVideo = (
  eventId: number,
  stageIndex: number,
  deviceId: string,
): Promise<FullVideoView | null> =>
  call(
    'POST',
    `/events/${eventId}/stages/${stageIndex}/devices/${encodeURIComponent(deviceId)}/full-video/cancel`,
  );

/** Use as a plain `<a href>`: the 302 must reach the browser as a native download. */
export function fullVideoDownloadUrl(
  eventId: number,
  stageIndex: number,
  deviceId: string,
  camera?: string,
): string {
  const qs = camera ? `?camera=${encodeURIComponent(camera)}` : '';
  return `${BASE}/events/${eventId}/stages/${stageIndex}/devices/${encodeURIComponent(deviceId)}/full-video/download${qs}`;
}

/** Per-stage corridor route (server-decimated polyline). */
export interface StageRouteInfo {
  stage_index: number;
  point_count: number;
  source_point_count: number | null;
  corridor_m: number;
  length_m: number | null;
  updated_at: string | null;
}

export const getStageRoute = (
  eventId: number,
  stageIndex: number,
): Promise<StageRouteInfo | null> =>
  call('GET', `/events/${eventId}/stages/${stageIndex}/route`);

export const putStageRoute = (
  eventId: number,
  stageIndex: number,
  points: Array<[number, number]>,
  corridorM: number,
): Promise<StageRouteInfo> =>
  call('PUT', `/events/${eventId}/stages/${stageIndex}/route`, {
    points,
    corridor_m: corridorM,
  });

export const deleteStageRoute = (
  eventId: number,
  stageIndex: number,
): Promise<{ deleted: boolean }> =>
  call('DELETE', `/events/${eventId}/stages/${stageIndex}/route`);

/** Public official stage geometry used by the event viewer map. */
export interface StageRouteGeometry {
  stage_index: number;
  name: string | null;
  points: Array<[number, number]>;
  corridor_m: number;
  length_m: number | null;
}

export const listEventStageRoutes = (eventId: number): Promise<StageRouteGeometry[]> =>
  call('GET', `/events/${eventId}/routes`);

// ── Laps (circuit events) ─────────────────────────────────────────────

export interface Lap {
  lap_number: number;
  event_id: number;
  device_id: string;
  session_id: string;
  start_utc_ms: number;
  end_utc_ms: number;
  duration_ms: number;
  end_lat: number | null;
  end_lon: number | null;
  /** 'live' | 'recompute' | 'recompute_s3' (degraded timing accuracy) */
  source: string;
  // Circuit Engine refinement — null/absent on unrefined laps (old rows,
  // engine off, live SSE laps). The UI must render exactly as before then.
  /** 1σ uncertainty of duration_ms, in milliseconds. */
  sigma_ms?: number | null;
  confidence?: 'high' | 'med' | 'low' | null;
  /** e.g. 'linear' | 'hermite' | 'hermite_imu' */
  method?: string | null;
  /** Signed engine-vs-chord END-CROSSING timestamp delta in ms
   *  (refined end_utc_ms minus the chord detector's) — NOT a
   *  lap-duration delta. */
  chord_delta_ms?: number | null;
}

export const listEventLaps = (
  eventId: number,
  deviceId?: string,
): Promise<Lap[]> =>
  call(
    'GET',
    `/events/${eventId}/laps${deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : ''}`,
  );

// ── Gates ─────────────────────────────────────────────────────────────

export interface GateCreate {
  kind: 'start' | 'finish' | 'lap_line';
  lat: number;
  lon: number;
  radius_m: number;
  stage_index?: number;
  /** Required when kind === 'lap_line' (crossing direction = A→B). */
  lat2?: number;
  lon2?: number;
}

export type GatePatch = Partial<GateCreate>;

export const addGate = (eventId: number, body: GateCreate): Promise<EventGate> =>
  call('POST', `/events/${eventId}/gates`, body);
export const updateGate = (
  eventId: number,
  gateId: number,
  patch: GatePatch,
): Promise<EventGate> => call('PUT', `/events/${eventId}/gates/${gateId}`, patch);
export const deleteGate = (
  eventId: number,
  gateId: number,
): Promise<{ id: number; deleted: boolean }> =>
  call('DELETE', `/events/${eventId}/gates/${gateId}`);

// ── Enrollments ───────────────────────────────────────────────────────

export type EnrollmentState = 'pending' | 'armed' | 'rejected' | 'disarmed';

export interface EnrollmentRow {
  id: number;
  event_id: number;
  device_id: string;
  state: EnrollmentState;
  driver_name: string | null;
  vehicle: string | null;
  /** Race number ("12", "12A"); operator-set on the roster. */
  car_number: string | null;
  /** Join-form filming role; null from legacy clients. */
  role: 'onboard' | 'trackside' | null;
  /** The driver's pitch to the organizer (join form).
   *  Operator/device-only; the backend redacts it for anonymous. */
  join_message: string | null;
  consent_given: boolean;
  created_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  disarmed_at: string | null;
  rejected_reason: string | null;
}

export const listEnrollments = (
  eventId: number,
  state?: EnrollmentState,
): Promise<EnrollmentRow[]> =>
  call('GET', `/events/${eventId}/enrollments${state ? `?state=${state}` : ''}`);

/** Edit roster labels (car #, driver, vehicle). Display-only:
 *  no state transition, no geofence interaction. Send only the keys
 *  being changed. */
export const patchEnrollmentLabels = (
  eventId: number,
  enrollmentId: number,
  patch: { car_number?: string | null; driver_name?: string | null; vehicle?: string | null },
): Promise<EnrollmentRow> =>
  call('PATCH', `/events/${eventId}/enrollments/${enrollmentId}`, patch);

export const approveEnrollment = (
  eventId: number,
  enrollmentId: number,
): Promise<EnrollmentRow> =>
  call('POST', `/events/${eventId}/enrollments/${enrollmentId}/approve`);

export const rejectEnrollment = (
  eventId: number,
  enrollmentId: number,
  reason?: string,
): Promise<EnrollmentRow> =>
  call('POST', `/events/${eventId}/enrollments/${enrollmentId}/reject`, { reason });

/** 'Same phone as…' — this enrollment adopts the labels + arm state
 *  of `fromEnrollmentId` (an accidental re-enrollment continues as
 *  the same car); the old row is retired with an audit reason. */
export const adoptEnrollment = (
  eventId: number,
  enrollmentId: number,
  fromEnrollmentId: number,
): Promise<EnrollmentRow> =>
  call('POST', `/events/${eventId}/enrollments/${enrollmentId}/adopt`, {
    from_enrollment_id: fromEnrollmentId,
  });

export const disarmEnrollment = (
  eventId: number,
  enrollmentId: number,
): Promise<EnrollmentRow> =>
  call('POST', `/events/${eventId}/enrollments/${enrollmentId}/disarm`);

export const rearmEnrollment = (
  eventId: number,
  enrollmentId: number,
): Promise<EnrollmentRow> =>
  call('POST', `/events/${eventId}/enrollments/${enrollmentId}/rearm`);

// ── Public join URL helper ────────────────────────────────────────────

/**
 * Canonical public-viewer-and-join URL for the event's QR poster.
 *
 * Pinned format: `https://tuvion.tv/join/<CODE>`
 * (or `.live` for staging). The scanner's regex requires this exact shape;
 * any drift here breaks the scan path. The event poster prints the join
 * code but the encoded payload is the URL.
 */
export function publicViewerUrl(joinCode: string | null): string {
  if (!joinCode) return '—';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://tuvion.tv';
  // Production deploys override origin to https://tuvion.tv via nginx; dev/staging
  // keeps the running host so QR-scan from-LAN still works.
  return `${origin}/join/${joinCode}`;
}

/**
 * Public spectator viewer URL for an event — the `/event/<id>` route.
 */
export function publicViewerEventUrl(eventId: number | null | undefined): string | null {
  if (eventId == null) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://tuvion.tv';
  return `${origin}/event/${eventId}`;
}
